import * as React from "react";
import classnames from "classnames";
import { PropertyDetails } from "../../../core/api/models/project";
import { FurnishingDetailsViewOption } from "./props";
import SortByAscendingIcon from "../../../assets/images/sort-by-ascending-icon";
import SortByDescendingIcon from "../../../assets/images/sort-by-descending-icon";
import ListViewIcon from "../../../assets/images/list-view-icon";
import MapViewIcon from "../../../assets/images/map-view-icon";
import { SelectedPropertiesMapView } from "./selected-properties-mapview";
import ArrowDownIcon from "../../../assets/images/arrow-down";
import { SelectedPropertiesDropdownTable } from "./selected-properties-dropdown-table";

const SelectedProperties: React.FunctionComponent<{
  requirementId?: string;
  sortList?: any[];
  onRemove: (propertys: PropertyDetails[]) => void;
  onRemoveExFlr?: (propertys: PropertyDetails[]) => void;
  onAddExFlr?: (propertys: PropertyDetails[]) => void;
  onRemoveExPhoto?: (propertys: PropertyDetails[]) => void;
  onAddExPhoto?: (propertys: PropertyDetails[]) => void;
  onRemoveExFurnishing?: (propertys: PropertyDetails[]) => void;
  onAddExFurnishing?: (propertys: PropertyDetails[]) => void;
  propertyDetails?: PropertyDetails[];
  excludeFloorDetails?: PropertyDetails[];
  excludePhotoDetails?: PropertyDetails[];
  excludeFurnishingDetails?: PropertyDetails[];
  onSelect: () => void;
  selectedFloorplanFurnishing?: { [key: string]: string };
  onUpdateFloorplanFurnishing?: (propertyId: string, value: string) => void;
  showThirdColumn?: { [key: string]: boolean };
  setShowThirdColumn?: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
}> = (props) => {
  const [propDropdown, setPropDropdown] = React.useState<number | null>(null);
  const [dropPos, setDropPos] = React.useState<{
    open: boolean;
    x: string;
    y: string;
    id: string;
    fieldName: string;
  }>();

  const dropdownClick = (event: any, id: string, fieldName: string) => {
    const pos = event.target.getBoundingClientRect();
    const x = pos.x + "px";
    const y = pos.y + pos.height + "px";
    setDropPos({ open: !dropPos?.open, x, y, id, fieldName });
  };

  const [sortOrder, setSortOrder] = React.useState("asc");
  const [seletedView, setSelectedView] = React.useState("list");
  const [locations, setLocations] = React.useState<
    { lat: number; lng: number; name: string }[] | undefined
  >([]);

  const sortList = !!props.propertyDetails?.length
    ? [
      ...new Map(
        props.propertyDetails?.map((item) => [item.propertyId, item] || [])
      ).values(),
    ]
    : [];

  const [groupByList, setGroupByList] = React.useState<any>();

  React.useEffect(() => {
    if (!!props.propertyDetails?.length) {
      const groupList = sortList
        ?.sort((a: any, b: any) => a.projectName?.localeCompare(b.projectName))
        ?.reduce((group: any, product: any) => {
          const { projectId } = product;

          group[projectId] = group[projectId] ?? [];
          group[projectId].push(product);
          return group;
        }, {});

      const tempLocations: { lat: number; lng: number; name: string }[] = [];
      if (groupList && Object.keys(groupList)?.length > 0) {
        Object.keys(groupList)?.map((key, index) => {
          const item = groupList[key]?.find((x: any) => x?.projectId === key);

          tempLocations.push({
            lat: parseFloat(item?.latitude),
            lng: parseFloat(item?.longitude),
            name: (index + 1).toString(),
          });
        });
      }

      setLocations(tempLocations);

      setGroupByList(groupList);
    }
  }, [props.propertyDetails]);

  const onSort = (sort: string) => {
    setSortOrder(sort);
    const sList =
      sort === "asc"
        ? sortList?.sort((a: any, b: any) =>
          a.projectName?.localeCompare(b.projectName)
        )
        : sortList?.sort((a: any, b: any) =>
          b.projectName?.localeCompare(a.projectName)
        );
    if (!!sList?.length) {
      const groupList = sList?.reduce((group: any, product: any) => {
        const { projectId } = product;

        group[projectId] = group[projectId] ?? [];
        group[projectId].push(product);
        return group;
      }, {});
      setGroupByList(groupList);
    }
  };

  const ordinalSuffixOf = (i: number) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  };

  const onAllSelected = (projectId: string) => {
    const currentShortList = sortList?.filter(
      (x) => x.projectId !== projectId?.toString()
    );
    props.onRemove(currentShortList!);
    onAllSelectedExcludedFloorPlan(false, projectId);
  };

  const onSelect = (propertyId: string) => {
    const currentShortList = sortList?.filter(
      (x) => x.propertyId !== propertyId?.toString()
    );
    props.onRemove(currentShortList!);
    onSelectExcludeFloorPlan(false, propertyId);
  };

  const onSelectExcludeFloorPlan = (sel: boolean, propertyId: any) => {
    if (sel) {
      const filteredPrptyDetails: any =
        props?.propertyDetails?.find(
          (prpDetls) => prpDetls?.propertyId === propertyId
        ) || [];
      const newArray = [
        ...(props?.excludeFloorDetails || []),
        filteredPrptyDetails,
      ];
      props?.onAddExFlr?.(newArray);
    } else {
      const filteredFlrVal: PropertyDetails[] =
        props?.excludeFloorDetails?.filter(
          (exFlr) => exFlr?.propertyId !== propertyId
        ) || [];
      props?.onRemoveExFlr?.(filteredFlrVal);
    }
  };

  const onAllSelectedExcludedFloorPlan = (sel: boolean, projectId: string) => {
    if (sel) {
      const currentExcludedFloor = sortList?.filter(
        (x) => x.projectId === projectId?.toString()
      );
      const existingExcludedFloor = props?.excludeFloorDetails || [];
      const newExcludedFloor = currentExcludedFloor.filter(
        (floor) =>
          !existingExcludedFloor.some(
            (existing: any) => existing.propertyId === floor.propertyId
          )
      );
      const newArray: any = [...existingExcludedFloor, ...newExcludedFloor];
      props?.onAddExFlr?.(newArray);
    } else {
      const currentRemovedExcludedFloor: any = props?.excludeFloorDetails?.filter(
        (x) => x.projectId !== projectId?.toString()
      );
      props?.onRemoveExFlr?.(currentRemovedExcludedFloor);
    }
  };

  const onSelectExcludePhotoGallery = (sel: boolean, propertyId: any) => {
    if (sel) {
      const filteredPrptyDetails: any =
        props?.propertyDetails?.find(
          (prpDetls) => prpDetls?.propertyId === propertyId
        ) || [];
      const newArray = [
        ...(props?.excludePhotoDetails || []),
        filteredPrptyDetails,
      ];
      props?.onAddExPhoto?.(newArray);
    } else {
      const filteredPhotoVal: PropertyDetails[] =
        props?.excludePhotoDetails?.filter(
          (exPhoto) => exPhoto?.propertyId !== propertyId
        ) || [];
      props?.onRemoveExPhoto?.(filteredPhotoVal);
    }
  };

  const onAllSelectedExcludePhotoGallery = (
    sel: boolean,
    projectId: string
  ) => {
    if (sel) {
      const currentExcludedPhoto = sortList?.filter(
        (x) => x.projectId === projectId?.toString()
      );
      const existingExcludedPhoto = props?.excludePhotoDetails || [];
      const newExcludedPhoto = currentExcludedPhoto.filter(
        (photo) =>
          !existingExcludedPhoto.some(
            (existing: any) => existing.propertyId === photo.propertyId
          )
      );
      const newArray: any = [...existingExcludedPhoto, ...newExcludedPhoto];
      props?.onAddExPhoto?.(newArray);
    } else {
      const currentRemovedExcludedPhoto: any = props?.excludePhotoDetails?.filter(
        (x) => x.projectId !== projectId?.toString()
      );
      props?.onRemoveExPhoto?.(currentRemovedExcludedPhoto);
    }
  };

  const onSelectExcludeFurnishing = (sel: boolean, propertyId: any) => {
    if (sel) {
      const filteredPrptyDetails: any =
        props?.propertyDetails?.find(
          (prpDetls) => prpDetls?.propertyId === propertyId
        ) || [];
      const newArray = [
        ...(props?.excludeFurnishingDetails || []),
        filteredPrptyDetails,
      ];
      props?.onAddExFurnishing?.(newArray);
    } else {
      const filteredVal: PropertyDetails[] =
        props?.excludeFurnishingDetails?.filter(
          (ex) => ex?.propertyId !== propertyId
        ) || [];
      props?.onRemoveExFurnishing?.(filteredVal);
    }
  };

  const onAllSelectedExcludeFurnishing = (sel: boolean, projectId: string) => {
    if (sel) {
      const currentExcluded = sortList?.filter(
        (x) => x.projectId === projectId?.toString()
      );
      const existingExcluded = props?.excludeFurnishingDetails || [];
      const newExcluded = currentExcluded.filter(
        (item) =>
          !existingExcluded.some(
            (existing: any) => existing.propertyId === item.propertyId
          )
      );
      const newArray: any = [...existingExcluded, ...newExcluded];
      props?.onAddExFurnishing?.(newArray);
    } else {
      const currentRemovedExcluded: any = props?.excludeFurnishingDetails?.filter(
        (x) => x.projectId !== projectId?.toString()
      );
      props?.onRemoveExFurnishing?.(currentRemovedExcluded);
    }
  };

  const handleThirdColumnChange = (projectId: string, checked: boolean) => {
    props.setShowThirdColumn?.((prev) => ({ ...prev, [projectId]: checked }));

    // Update all properties in this project to ThreeColumn
    const projectProperties =
      props.propertyDetails?.filter((p) => p.projectId === projectId) || [];
    projectProperties.forEach((item) => {
      props.onUpdateFloorplanFurnishing?.(
        item.propertyId || "",
        checked
          ? FurnishingDetailsViewOption.ThreeColumn
          : FurnishingDetailsViewOption.TwoColumn
      );
    });
  };

  return !!sortList?.length ? (
    <>
      <div
        className="py-2 px-4 border-top border-bottom d-flex align-items-center mb-1 project-tab-header justify-content-between"
        style={{ background: "rgba(160, 160, 160, 0.06)" }}
      >
        <div className="d-flex">
          <div className="font-sm text-gray-500">
            <span className="text-gray-700 font-weight-bolder">
              {sortList?.length}
            </span>{" "}
            Properties,{" "}
            <span className="text-gray-700 font-weight-bolder">
              {groupByList ? Object.keys(groupByList)?.length : 0}
            </span>{" "}
            Projects
          </div>
          <div
            className="inline-flex font-xs text-gray-500 border-left ml-4 pl-4"
            onClick={() => onSort(sortOrder === "asc" ? "dece" : "asc")}
          >
            {sortOrder === "asc" ? (
              <SortByAscendingIcon />
            ) : (
              <SortByDescendingIcon />
            )}
            <span className="ml-1">Sort</span>
          </div>
        </div>
        <div>
          <ul className="click-tab bg-white d-flex list-unstyled mb-0">
            <li
              className={classnames({
                "px-3 py-2 border border-right-0 rounded-left cursor-pointer": true,
                active: seletedView === "list",
              })}
              onClick={() => setSelectedView("list")}
            >
              <ListViewIcon />
            </li>
            <li
              className={classnames({
                "px-3 py-2 border rounded-right cursor-pointer": true,
                active: seletedView === "map",
              })}
              onClick={() => setSelectedView("map")}
            >
              <MapViewIcon />
            </li>
          </ul>
        </div>
      </div>

      {seletedView === "map" && (
        <SelectedPropertiesMapView locations={locations} groupByList={groupByList}></SelectedPropertiesMapView>
      )}

      {seletedView === "list" && (
        <>
          {groupByList && (
            <>
              <div className="table-responsive shortListProperty-Table">
                <table className="table w-100">
                  <tr className="font-sm text-gray-600">
                    <th className="py-3 border-bottom"></th>
                    <th
                      className="py-3 border-bottom"
                      style={{ maxWidth: "90px", width: "90px" }}
                    ></th>
                    <th
                      className="py-3 border-bottom font-weight-semibold "
                      style={{ width: "290px", maxWidth: "290px" }}
                    >
                      Property
                    </th>
                    <th
                      className="py-3 border-bottom font-weight-semibold  text-center"
                      style={{ width: "194px" }}
                    >
                      Version
                    </th>
                    <th className="py-3 border-bottom font-weight-semibold ">
                      Square feet area
                    </th>
                    <th className="py-3 border-bottom font-weight-semibold ">
                      Developer
                    </th>

                    <th className="py-3 border-bottom font-weight-semibold ">
                      Added Units
                    </th>
                    <th className="py-3 border-bottom font-weight-semibold">
                      Floorplan & Furnishing Details
                    </th>
                  </tr>
                  {Object.keys(groupByList).map((key, index) => {
                    const item = groupByList[key]?.find(
                      (x: any) => x.projectId === key
                    );

                    const totalArea =
                      groupByList[key]?.reduce(
                        (t: any, c: any) => t + Number(c.area || 0),
                        0
                      ) || 0;

                    const unitDetails = groupByList[
                      key
                    ]?.sort((a: any, b: any) => (a.floor < b.floor ? -1 : 1));

                    const floors = [
                      ...new Set(unitDetails?.map((f: any) => f.floor)),
                    ].sort();

                    return (
                      <>
                        <tr
                          className={`require-row ${propDropdown === index ? "active" : ""
                            }`}
                        >
                          <td
                            className="py-2 border-bottom"
                            style={{ maxWidth: "40px", width: "40px" }}
                          >
                            <div className="py-1">
                              <i
                                className="icon-close cursor-pointer"
                                onClick={(e) => onAllSelected(item.projectId)}
                              ></i>
                            </div>
                          </td>
                          <td
                            className="py-2 border-bottom"
                            style={{ maxWidth: "90px", width: "90px" }}
                          >
                            <div className="py-1 text-center">
                              <img
                                src={item?.propertyCoverImage}
                                alt={item?.projectName}
                                width={59}
                                height={45}
                                className="img-cover"
                              />
                            </div>
                          </td>
                          <td
                            className="py-2 border-bottom"
                            style={{ width: "290px", maxWidth: "290px" }}
                          >
                            <div className="py-1 w-100">
                              <div className="font-sm text-truncate mb-1">
                                {item?.projectName}
                              </div>
                              <div className="font-xs text-gray-500 line-clamp-2">
                                {item?.propertyAddress}
                              </div>
                            </div>
                          </td>
                          <td
                            className="py-2 border-bottom"
                            style={{ width: "194px" }}
                          >
                            <div className="py-1 font-sm text-center">
                              V{item?.requirementVersion}
                            </div>
                          </td>
                          <td className="py-2 border-bottom">
                            <div className="py-1 font-xs text-gray-800">
                              <b>{totalArea?.toFixed(2)}</b> /Sq. Ft
                            </div>
                          </td>
                          <td className="py-2 border-bottom">
                            <div className="py-1 font-xs text-gray-800 font-weight-semibold">
                              {item?.developer}
                            </div>
                          </td>
                          <td className="py-2 border-bottom">
                            <div className="py-1">
                              <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                                Floor: {groupByList[key][0]?.floor} / Unit:{" "}
                                {groupByList[key][0]?.unitName} / Area:{" "}
                                {groupByList[key][0]?.area}
                              </div>
                              {groupByList[key]?.length > 1 && (
                                <div className="font-xs text-gray-600 border rounded-pill d-inline-flex py-1 px-2 align-items-center mr-2">
                                  +{groupByList[key]?.length - 1}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 border-bottom">
                            <div
                              className="py-1 cursor-pointer d-flex justify-content-center"
                              onClick={() =>
                                propDropdown === index
                                  ? setPropDropdown(null)
                                  : setPropDropdown(index)
                              }
                            >
                              <ArrowDownIcon />
                            </div>
                          </td>
                        </tr>
                        {propDropdown === index && (
                          <tr>
                            <td colSpan={8}>
                              <SelectedPropertiesDropdownTable
                                dropdownClick={dropdownClick}
                                dropPos={dropPos}
                                floors={floors}
                                handleThirdColumnChange={handleThirdColumnChange}
                                item={item}
                                onAllSelected={onAllSelected}
                                onAllSelectedExcludeFurnishing={onAllSelectedExcludeFurnishing}
                                onAllSelectedExcludePhotoGallery={onAllSelectedExcludePhotoGallery}
                                onSelect={onSelect}
                                onSelectExcludeFloorPlan={onSelectExcludeFloorPlan}
                                onSelectExcludeFurnishing={onSelectExcludeFurnishing}
                                onSelectExcludePhotoGallery={onSelectExcludePhotoGallery}
                                ordinalSuffixOf={ordinalSuffixOf}
                                selectedFloorplanFurnishing={props.selectedFloorplanFurnishing}
                                showThirdColumn={props.showThirdColumn}
                                unitDetails={unitDetails}
                                onAllSelectedExcludedFloorPlan={onAllSelectedExcludedFloorPlan}
                                excludeFloorDetails={props.excludeFloorDetails}
                                excludeFurnishingDetails={props.excludeFurnishingDetails}
                                excludePhotoDetails={props.excludePhotoDetails}
                                onUpdateFloorplanFurnishing={props.onUpdateFloorplanFurnishing}
                                propertyDetails={props.propertyDetails}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </table>
              </div>
              <div className="d-flex justify-content-end mt-3 ">
                <div>
                  <span className="pr-3 font-sm">
                    {!!sortList?.length && (
                      <>
                        <span className="pr-2 text-gray-600">
                          <span className="font-weight-bold text-gray-700">
                            {
                              [...new Set(sortList?.map((x) => x.propertyId))]
                                ?.length
                            }{" "}
                          </span>
                          Properties
                        </span>

                        <span className="text-gray-600">
                          <span className="font-weight-bold text-gray-700">
                            {
                              [...new Set(sortList?.map((x) => x.projectId))]
                                ?.length
                            }{" "}
                          </span>
                          Projects
                        </span>
                      </>
                    )}
                  </span>

                  <button
                    type="button"
                    onClick={props.onSelect}
                    className="btn bg-primary select-prop-bt font-normal font-weight-bold text-white"
                    disabled={!props.propertyDetails?.length}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  ) : (
    <></>
  );
};

export default SelectedProperties;
